// Công nợ phải thu (Mint-AR): suy từ ArCustomer của K (đọc, không sửa). Tiền = triệu đồng; chỉ màn có quyền `money` được dùng.
import type { ArCustomer } from '../finance/types';
import { AR_CUSTOMERS } from '../finance/fixtures';

export type ArFlag = 'Chặn release' | 'Giữ DO' | '';

export interface ArRow extends ArCustomer {
  /** quá hạn = 1–30 + 31–60 + 61–90 + > 90 */
  over: number;
  /** phải thu = chưa đến hạn + quá hạn */
  total: number;
  /** quá hạn > 90 ngày hoặc vượt hạn mức → chặn release B/L / giao DO (KTT / BOD duyệt) */
  block: boolean;
  /** quá hạn > 30 ngày (chưa chặn) → giữ DO (Trưởng KD duyệt) */
  hold: boolean;
  flag: ArFlag;
  /** mốc nhắc nợ kế tiếp trên nút "Nhắc nợ"; '' nếu không quá hạn */
  mark: '' | '+7' | '+30';
}

export function arRow(c: ArCustomer): ArRow {
  const over = c.d1_30 + c.d31_60 + c.d61_90 + c.d90;
  const total = c.current + over;
  const block = c.d90 > 0 || (c.limit > 0 && total > c.limit); // limit 0 = chưa có hạn mức (dữ liệu thật)
  const hold = !block && c.d31_60 + c.d61_90 > 0;
  return { ...c, over, total, block, hold, flag: block ? 'Chặn release' : hold ? 'Giữ DO' : '', mark: c.d31_60 + c.d61_90 + c.d90 > 0 ? '+30' : c.d1_30 > 0 ? '+7' : '' };
}

export const arRows = (src: readonly ArCustomer[] = AR_CUSTOMERS): ArRow[] => src.map(arRow);

export type ArSortKey = 'over' | 'limit' | 'total' | 'd90' | 'dso';

export interface ArFilter { q?: string; onlyOver?: boolean }

export function filterAr(rows: readonly ArRow[], f: ArFilter = {}): ArRow[] {
  const q = f.q?.trim().toLowerCase();
  return rows.filter((r) => (!q || r.customer.toLowerCase().includes(q) || r.sale.toLowerCase().includes(q)) && (!f.onlyOver || r.over > 0));
}

export function sortAr(rows: readonly ArRow[], key: ArSortKey, dir: 'asc' | 'desc' = 'desc'): ArRow[] {
  const get = (r: ArRow): number => (key === 'limit' ? r.limit - r.total : r[key]);
  const s = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => s * (get(a) - get(b)));
}

export interface ArSummary { total: number; current: number; b1: number; b2: number; b3: number; b4: number; over: number; dso: number; held: number }

/** DSO chung = bình quân DSO từng khách theo trọng số phải thu. */
export function arSummary(rows: readonly ArRow[]): ArSummary {
  const sum = (f: (r: ArRow) => number): number => rows.reduce((s, r) => s + f(r), 0);
  const total = sum((r) => r.total);
  return {
    total, current: sum((r) => r.current), b1: sum((r) => r.d1_30), b2: sum((r) => r.d31_60), b3: sum((r) => r.d61_90), b4: sum((r) => r.d90),
    over: sum((r) => r.over), dso: rows.length ? Math.round(sum((r) => r.dso * r.total) / Math.max(1, total)) : 0, held: rows.filter((r) => r.flag).length,
  };
}

/** Chi hộ treo theo khách (Mint-AR `__PAOBH`): [số tiền tr, trạng thái dòng, quá hạn vào debit?]. Chỉ dòng chưa 'paid' tính là treo. */
type ObhLine = readonly [number, 'dn' | 'nodn' | 'wait' | 'paid', number];
const OBH: Readonly<Record<string, { mode: 'adv' | 'pre'; cap: number; ln: readonly ObhLine[] }>> = {
  SAVITA: { mode: 'adv', cap: 50, ln: [[0.5, 'dn', 0], [0.3, 'dn', 0]] },
  NKL: { mode: 'adv', cap: 100, ln: [[2.4, 'dn', 0], [1.8, 'dn', 0], [0.6, 'nodn', 3]] },
  THUANPHATCM: { mode: 'pre', cap: 320, ln: [[312.6, 'dn', 0], [3.2, 'wait', 0]] },
  EmdeesFoods: { mode: 'adv', cap: 80, ln: [[4.2, 'nodn', 2], [88.3, 'wait', 0]] },
  DAVICO: { mode: 'adv', cap: 20, ln: [[1.1, 'nodn', 9], [2.0, 'dn', 0]] },
  TAMNGOC: { mode: 'adv', cap: 50, ln: [[6.8, 'dn', 0]] },
  MKAGRISEA: { mode: 'pre', cap: 15, ln: [[12.0, 'wait', 0]] },
  DSF2012: { mode: 'pre', cap: 58.4, ln: [[58.4, 'paid', 0]] },
};

export interface ArObh { open: number; red: boolean }

/** Chi hộ treo của khách; `red` khi vượt hạn mức ứng / tạm thu hoặc có dòng quá hạn vào debit. */
export function arObh(customer: string): ArObh {
  const c = OBH[customer];
  if (!c) return { open: 0, red: false };
  const open = c.ln.filter((l) => l[1] !== 'paid').reduce((a, l) => a + l[0], 0);
  const used = c.mode === 'adv' ? open : c.ln.reduce((a, l) => a + l[0], 0);
  return { open, red: used > c.cap + 0.001 || c.ln.some((l) => l[1] === 'nodn' && l[2] > 0) };
}

export const arObhCount = (rows: readonly ArRow[]): number => rows.filter((r) => arObh(r.customer).open > 0).length;

/** Bộ lọc nhanh của iPad / điện thoại (Mint-AR-iPad · -Phone): chip có số đếm. */
export type ArChip = 'all' | 'over' | 'hold' | 'mine' | 'obh';
export function arChipFilter(rows: readonly ArRow[], chip: ArChip, me = 'DAVID'): ArRow[] {
  if (chip === 'over') return rows.filter((r) => r.over > 0);
  if (chip === 'hold') return rows.filter((r) => r.flag);
  if (chip === 'mine') return rows.filter((r) => r.sale === me);
  if (chip === 'obh') return rows.filter((r) => arObh(r.customer).open > 0);
  return [...rows];
}
